import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { PriceListApiService } from '../services/price-list-api.service';
import { ProductoApiService } from '../services/producto-api.service';
import {
  PriceListRequest,
  PriceListResponse,
  PriceListItemRequest,
  PriceListItemResponse,
} from '../models/price-list.model';
import { CurrencyResponse, ProductResponse } from '../models/producto.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { currencyFractionDigits } from '../../../shared/utils/currency-format.utils';

@Component({
  selector: 'app-price-list-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
  ],
  templateUrl: './price-list-form.component.html',
})
export class PriceListFormComponent implements OnInit {
  @Input() drawerMode = false;
  @Output() saved = new EventEmitter<PriceListResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  priceListId: number | null = null;
  businessCurrency: number | null | undefined = null;
  currentPriceList: PriceListResponse | null = null;

  currencies: CurrencyResponse[] = [];

  items: PriceListItemResponse[] = [];
  loadingItems = false;

  // Item inline form
  showItemForm = false;
  itemForm!: FormGroup;

  productSearch = '';
  productResults: ProductResponse[] = [];
  searchingProducts = false;
  selectedProduct: ProductResponse | null = null;
  itemEnEdicion: PriceListItemResponse | null = null;

  private businessId!: number;
  private productsById = new Map<number, ProductResponse>();

  private itemsOriginales: PriceListItemResponse[] = [];
  private itemsEliminadosIds: number[] = [];
  private itemPersistError = '';

  constructor(
    private fb: FormBuilder,
    private priceListApi: PriceListApiService,
    private productApi: ProductoApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    this.businessId = user?.businessId || 1;

    this.initForm();
    this.initItemForm();
    this.cargarMonedas();

    this.form.get('currencyId')?.valueChanges.subscribe(() => {
      this.recalcularPreciosPorMoneda();
    });

    if (!this.drawerMode) {
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.isEditing = true;
        this.priceListId = +id;
        this.cargarPriceList(this.priceListId);
      }
    }
  }

  reset(): void {
    this.isEditing = false;
    this.priceListId = null;
    this.currentPriceList = null;
    this.items = [];
    this.itemsOriginales = [];
    this.itemsEliminadosIds = [];
    this.error = '';
    this.saving = false;
    this.showItemForm = false;
    this.initForm();
    this.initItemForm();
  }

  loadPriceList(id: number): void {
    this.isEditing = true;
    this.priceListId = id;
    this.cargarPriceList(id);
  }

  private initForm(): void {
    this.businessCurrency =
      this.authService.getCurrentBusiness()?.defaultCurrencyId;
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(200)]],
      description: ['', Validators.maxLength(500)],
      currencyId: [this.businessCurrency],
      isDefault: [false],
    });
  }

  private initItemForm(): void {
    this.itemForm = this.fb.group({
      productId: [null, Validators.required],
      priceType: ['fixed', Validators.required],
      price: [null],
      discountPercent: [null],
      discountAmount: [null],
      minQuantity: [1, [Validators.required, Validators.min(1)]],
    });

    this.itemForm.get('priceType')?.valueChanges.subscribe(() => {
      this.actualizarValidacionesPrecio();
    });
  }

  private cargarMonedas(): void {
    this.productApi.getCurrencies().subscribe({
      next: (data) => {
        this.currencies = data;
        this.recalcularPreciosPorMoneda();
      },
      error: () => {},
    });
  }

  cargarPriceList(id: number): void {
    this.loading = true;
    this.priceListApi.getPriceListById(id).subscribe({
      next: (pl) => {
        this.currentPriceList = pl;
        this.form.patchValue({
          name: pl.name,
          description: pl.description || '',
          currencyId: pl.currencyId || null,
          isDefault: pl.isDefault,
        });
        this.loading = false;
        this.cargarItems();
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la lista';
        this.loading = false;
      },
    });
  }

  private cargarItems(): void {
    if (!this.priceListId) return;
    this.loadingItems = true;
    this.priceListApi.getPriceListItems(this.priceListId).subscribe({
      next: (data) => {
        this.items = data;
        this.itemsOriginales = data.map((it) => ({ ...it }));
        this.loadingItems = false;
        this.cargarDatosProductosDeItems();
      },
      error: () => {
        this.items = [];
        this.loadingItems = false;
      },
    });
  }

  private itemsCambiaron(): boolean {
    if (this.itemsEliminadosIds.length > 0) return true;
    if (this.items.length !== this.itemsOriginales.length) return true;
    return this.items.some((it) => {
      const orig = this.itemsOriginales.find((o) => o.id === it.id);
      if (!orig) return true;
      return (
        orig.productId !== it.productId ||
        orig.priceType !== it.priceType ||
        orig.price !== it.price ||
        orig.discountPercent !== it.discountPercent ||
        orig.discountAmount !== it.discountAmount ||
        orig.minQuantity !== it.minQuantity
      );
    });
  }

  private cargarDatosProductosDeItems(): void {
    const idsUnicos = [...new Set(this.items.map((it) => it.productId))];
    const idsPorCargar = idsUnicos.filter((id) => !this.productsById.has(id));

    if (idsPorCargar.length === 0) {
      this.recalcularPreciosPorMoneda();
      return;
    }

    let restantes = idsPorCargar.length;
    idsPorCargar.forEach((id) => {
      this.productApi.getProductById(id, this.businessId).subscribe({
        next: (product) => {
          this.productsById.set(id, product);
          restantes--;
          if (restantes === 0) this.recalcularPreciosPorMoneda();
        },
        error: () => {
          restantes--;
          if (restantes === 0) this.recalcularPreciosPorMoneda();
        },
      });
    });
  }

  get pageTitle(): string {
    return this.isEditing
      ? 'Editar Lista de Precios'
      : 'Nueva Lista de Precios';
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // Inc 43/50: valida en el cliente que cada producto tenga precio (>0) y
    // cantidad mínima (>=1) ANTES de crear la lista. No se permite creación parcial.
    const itemError = this.validarItemsAntesDeCrear();
    if (itemError) {
      this.error = itemError;
      this.toast.error({ title: 'Revisa los productos', message: itemError });
      return;
    }

    if (this.form.pristine && !this.itemsCambiaron()) {
      this.toast.info({
        title: 'Lista de precios',
        message:
          'No se realizaron nuevos cambios en la regla de lista de precios',
      });
      this.router.navigate(['/productos/listas-precios']);
      return;
    }

    this.saving = true;
    this.error = '';

    const v = this.form.value;
    const request: PriceListRequest = {
      name: v.name,
      description: v.description || undefined,
      currencyId: v.currencyId || undefined,
      isDefault: v.isDefault,
      businessId: this.businessId,
    };

    const request$ =
      this.isEditing && this.priceListId
        ? this.priceListApi.updatePriceList(this.priceListId, request)
        : this.priceListApi.createPriceList(request);

    request$.subscribe({
      next: (pl) => {
        this.persistirItemsLocales(pl);
      },
      error: (err) => {
        this.saving = false;
        this.error =
          err.error?.message ||
          (this.isEditing ? 'Error al actualizar' : 'Error al crear');
      },
    });
  }

  private validarItemsAntesDeCrear(): string | null {
    for (const it of this.items) {
      const nombre = this.getProductName(it.productId);
      if (
        it.priceType === 'fixed' &&
        (it.price === null || it.price === undefined || it.price <= 0)
      ) {
        return `El producto "${nombre}" no tiene un precio válido (debe ser mayor que cero).`;
      }
      if (
        it.priceType === 'discount_percent' &&
        (it.discountPercent === null ||
          it.discountPercent === undefined ||
          it.discountPercent <= 0)
      ) {
        return `El producto "${nombre}" no tiene un porcentaje de descuento válido.`;
      }
      if (
        it.priceType === 'discount_amount' &&
        (it.discountAmount === null ||
          it.discountAmount === undefined ||
          it.discountAmount <= 0)
      ) {
        return `El producto "${nombre}" no tiene un monto de descuento válido.`;
      }
      if (it.minQuantity === null || it.minQuantity === undefined) {
        return `Cantidad mínima es un campo obligatorio para "${nombre}".`;
      }
      if (it.minQuantity < 1) {
        return `La cantidad mínima de "${nombre}" debe ser mayor o igual a 1.`;
      }
    }
    return null;
  }

  onFocusProducto(): void {
    if (this.productSearch.length >= 2) return;
    this.searchingProducts = true;
    this.productApi
      .searchProduct({
        businessId: this.businessId,
        page: 0,
        size: 10,
      })
      .subscribe({
        next: (res) => {
          this.productResults = res.content;
          this.searchingProducts = false;
        },
        error: () => {
          this.productResults = [];
          this.searchingProducts = false;
        },
      });
  }

  onBlurProducto(): void {
    setTimeout(() => {
      this.productResults = [];
    }, 200);
  }

  private persistirItemsLocales(priceList: PriceListResponse): void {
    const itemsAGuardar = [...this.items];
    const idsAEliminar = [...this.itemsEliminadosIds];
    let exitosos = 0;
    let fallidos = 0;
    let completados = 0;
    const total = itemsAGuardar.length + idsAEliminar.length;
    this.itemPersistError = '';

    if (total === 0) {
      this.finalizarGuardado(priceList);
      return;
    }

    itemsAGuardar.forEach((item) => {
      const req: PriceListItemRequest = {
        productId: item.productId,
        priceType: item.priceType,
        price:
          item.priceType === 'fixed' ? (item.price ?? undefined) : undefined,
        discountPercent:
          item.priceType === 'discount_percent'
            ? (item.discountPercent ?? undefined)
            : undefined,
        discountAmount:
          item.priceType === 'discount_amount'
            ? (item.discountAmount ?? undefined)
            : undefined,
        minQuantity: item.minQuantity ?? undefined,
      };

      this.priceListApi.upsertPriceListItem(priceList.id, req).subscribe({
        next: () => {
          exitosos++;
          completados++;
          this.checkPersistenciaCompleta(
            priceList,
            exitosos,
            fallidos,
            total,
            completados,
          );
        },
        error: (err) => {
          fallidos++;
          completados++;
          if (err.error?.message) this.itemPersistError = err.error.message;
          this.checkPersistenciaCompleta(
            priceList,
            exitosos,
            fallidos,
            total,
            completados,
          );
        },
      });
    });

    idsAEliminar.forEach((id) => {
      this.priceListApi
        .deletePriceListItem(priceList.id, id, this.businessId)
        .subscribe({
          next: () => {
            exitosos++;
            completados++;
            this.checkPersistenciaCompleta(
              priceList,
              exitosos,
              fallidos,
              total,
              completados,
            );
          },
          error: (err) => {
            fallidos++;
            completados++;
            if (err.error?.message) this.itemPersistError = err.error.message;
            this.checkPersistenciaCompleta(
              priceList,
              exitosos,
              fallidos,
              total,
              completados,
            );
          },
        });
    });
  }

  private checkPersistenciaCompleta(
    priceList: PriceListResponse,
    exitosos: number,
    fallidos: number,
    total: number,
    completados: number,
  ): void {
    if (completados < total) return;

    this.saving = false;
    if (fallidos > 0) {
      this.toast.error({
        title: this.isEditing
          ? 'Lista actualizada con advertencias'
          : 'Lista creada con advertencias',
        message: this.itemPersistError
          ? `${this.itemPersistError} (${fallidos} de ${total} cambios no se aplicaron).`
          : `Se guardó la lista pero ${fallidos} de ${total} cambios de productos no se pudieron aplicar. Intenta nuevamente.`,
      });
    } else {
      this.toast.success({
        title: this.isEditing ? 'Lista actualizada' : 'Lista creada',
        message: 'Los cambios se guardaron correctamente.',
      });
    }

    this.itemsOriginales = this.items.map((it) => ({ ...it }));
    this.itemsEliminadosIds = [];

    if (this.drawerMode) {
      this.saved.emit(priceList);
    } else {
      this.router.navigate(['/productos/listas-precios']);
    }
  }

  private finalizarGuardado(priceList: PriceListResponse): void {
    this.saving = false;
    this.toast.success({
      title: this.isEditing ? 'Lista actualizada' : 'Lista creada',
      message: this.isEditing
        ? 'Los cambios se guardaron correctamente.'
        : 'La lista se creó correctamente.',
    });
    if (this.drawerMode) {
      this.saved.emit(priceList);
    } else {
      this.router.navigate(['/productos/listas-precios']);
    }
  }

  cancelar(): void {
    if (this.drawerMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/productos/listas-precios']);
    }
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return c ? c.invalid && c.touched : false;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c || !c.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength'])
      return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    return 'Campo inválido';
  }

  get descriptionLength(): number {
    return this.form.get('description')?.value?.length ?? 0;
  }

  abrirItemForm(): void {
    this.itemEnEdicion = null;
    this.selectedProduct = null;
    this.productSearch = '';
    this.productResults = [];
    this.itemForm.reset({
      productId: null,
      priceType: 'fixed',
      price: null,
      discountPercent: null,
      discountAmount: null,
      minQuantity: 1,
    });
    this.actualizarValidacionesPrecio();
    this.showItemForm = true;
  }

  editarItem(it: PriceListItemResponse): void {
    this.itemEnEdicion = it;

    const prod = this.productsById.get(it.productId);
    this.selectedProduct = prod ?? null;
    this.productSearch = prod?.name ?? this.getProductName(it.productId);
    this.productResults = [];

    this.itemForm.reset({
      productId: it.productId,
      priceType: it.priceType,
      price: it.price ?? null,
      discountPercent: it.discountPercent ?? null,
      discountAmount: it.discountAmount ?? null,
      minQuantity: it.minQuantity ?? null,
    });
    this.actualizarValidacionesPrecio();
    this.showItemForm = true;
  }

  cerrarItemForm(): void {
    this.showItemForm = false;
    this.selectedProduct = null;
    this.productSearch = '';
    this.productResults = [];
    this.itemEnEdicion = null;
  }

  buscarProductos(): void {
    if (this.productSearch.length < 2) {
      this.productResults = [];
      return;
    }
    this.searchingProducts = true;
    this.productApi
      .searchProduct({
        businessId: this.businessId,
        searchName: this.productSearch,
        page: 0,
        size: 10,
      })
      .subscribe({
        next: (res) => {
          this.productResults = res.content;
          this.searchingProducts = false;
        },
        error: () => {
          this.productResults = [];
          this.searchingProducts = false;
        },
      });
  }

  seleccionarProducto(product: ProductResponse): void {
    this.selectedProduct = product;
    this.itemForm.patchValue({ productId: product.id });
    this.productResults = [];
    this.productSearch = product.name;
  }

  get resolvedPreview(): number | null {
    if (!this.selectedProduct) return null;

    const monedaDestino = this.monedaListaCode;
    const base = this.convertirMonto(
      this.selectedProduct.price,
      this.selectedProduct.currencyCode ?? 'USD',
      monedaDestino,
    );

    const v = this.itemForm.value;

    if (v.priceType === 'fixed' && v.price !== null) {
      return v.price;
    }
    if (v.priceType === 'discount_percent' && v.discountPercent !== null) {
      return base - (base * v.discountPercent) / 100;
    }
    if (v.priceType === 'discount_amount' && v.discountAmount !== null) {
      const descuentoConvertido = this.convertirMonto(
        v.discountAmount,
        this.selectedProduct.currencyCode ?? 'USD',
        monedaDestino,
      );
      return base - descuentoConvertido;
    }
    return base;
  }

  guardarItem(): void {
    this.itemForm.markAllAsTouched();

    if (this.itemForm.invalid) return;

    const v = this.itemForm.value;

    if (!v.productId) {
      return;
    }

    if (
      v.priceType === 'fixed' &&
      (v.price === null || v.price === undefined || v.price <= 0)
    ) {
      this.itemForm.get('price')?.setErrors({ min: { min: 0.01 } });
      return;
    }
    if (
      v.priceType === 'discount_percent' &&
      (v.discountPercent === null ||
        v.discountPercent === undefined ||
        v.discountPercent <= 0)
    ) {
      this.itemForm.get('discountPercent')?.setErrors({ min: { min: 0.01 } });
      return;
    }
    if (
      v.priceType === 'discount_amount' &&
      (v.discountAmount === null ||
        v.discountAmount === undefined ||
        v.discountAmount <= 0)
    ) {
      this.itemForm.get('discountAmount')?.setErrors({ min: { min: 0.01 } });
      return;
    }

    if (
      v.minQuantity !== null &&
      v.minQuantity !== undefined &&
      v.minQuantity < 1
    ) {
      this.itemForm.get('minQuantity')?.setErrors({ min: { min: 1 } });
      return;
    }

    if (this.itemEnEdicion) {
      this.reemplazarItemLocal(this.itemEnEdicion, v);
      this.cerrarItemForm();
      return;
    }

    const existente = this.items.find((it) => it.productId === v.productId);
    if (existente) {
      this.confirmarReemplazoLocal(existente, v);
      return;
    }
    this.agregarItemLocal(v);
    this.cerrarItemForm();
    return;
  }

  private reemplazarItemLocal(original: PriceListItemResponse, v: any): void {
    this.items = this.items.map((it) =>
      it.id === original.id
        ? {
            ...it,
            productId: v.productId,
            productName: this.selectedProduct?.name ?? it.productName,
            priceType: v.priceType,
            price: v.priceType === 'fixed' ? (v.price ?? undefined) : undefined,
            discountPercent:
              v.priceType === 'discount_percent'
                ? (v.discountPercent ?? undefined)
                : undefined,
            discountAmount:
              v.priceType === 'discount_amount'
                ? (v.discountAmount ?? undefined)
                : undefined,
            minQuantity: v.minQuantity ?? undefined,
            resolvedPrice: this.resolvedPreview ?? it.resolvedPrice,
          }
        : it,
    );
    if (this.selectedProduct) {
      this.productsById.set(this.selectedProduct.id, this.selectedProduct);
    }
  }

  private async confirmarReemplazoLocal(
    existente: PriceListItemResponse,
    v: any,
  ): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: 'Producto ya agregado',
      message: `El producto "${this.getProductName(existente.productId)}" ya existe en esta lista. ¿Deseas reemplazar la configuración actual?`,
      confirmText: 'Reemplazar',
      cancelText: 'Cancelar',
    });
    if (!ok) return;

    // Reemplazar el item local
    this.items = this.items.map((it) =>
      it.productId === existente.productId
        ? {
            ...it,
            priceType: v.priceType,
            price: v.priceType === 'fixed' ? (v.price ?? undefined) : undefined,
            discountPercent:
              v.priceType === 'discount_percent'
                ? (v.discountPercent ?? undefined)
                : undefined,
            discountAmount:
              v.priceType === 'discount_amount'
                ? (v.discountAmount ?? undefined)
                : undefined,
            minQuantity: v.minQuantity ?? undefined,
            resolvedPrice: this.resolvedPreview ?? it.resolvedPrice,
          }
        : it,
    );
    this.cerrarItemForm();
  }

  /**
   * Agrega un item nuevo al array local con un ID temporal negativo.
   */
  private agregarItemLocal(v: any): void {
    const tempId = -(Date.now() + this.items.length);
    const nuevoItem: PriceListItemResponse = {
      id: tempId,
      productId: v.productId,
      productName: this.selectedProduct?.name,
      priceType: v.priceType,
      price: v.priceType === 'fixed' ? (v.price ?? undefined) : undefined,
      discountPercent:
        v.priceType === 'discount_percent'
          ? (v.discountPercent ?? undefined)
          : undefined,
      discountAmount:
        v.priceType === 'discount_amount'
          ? (v.discountAmount ?? undefined)
          : undefined,
      minQuantity: v.minQuantity ?? undefined,
      resolvedPrice: this.resolvedPreview ?? undefined,
    } as PriceListItemResponse;

    this.items = [...this.items, nuevoItem];

    // Cachear el producto para los helpers getProductName/getProductSku
    if (this.selectedProduct) {
      this.productsById.set(this.selectedProduct.id, this.selectedProduct);
    }
  }

  async eliminarItem(item: PriceListItemResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar item',
      message: '¿Eliminar este item?',
    });
    if (!ok) return;

    this.items = this.items.filter((it) => it.id !== item.id);

    if (item.id > 0) {
      this.itemsEliminadosIds.push(item.id);
    }
  }

  formatPrecio(price?: number, currencyCode?: string): string {
    if (price === undefined || price === null) return '-';
    const code = currencyCode || 'USD';
    try {
      // Sin minimumFractionDigits: Intl ya usa la unidad mínima ISO 4217 de la
      // moneda (0 en JPY, 2 en USD, 3 en KWD). Forzar 2 pintaba importes
      // inexistentes en las monedas sin céntimos.
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: code,
      }).format(price);
    } catch {
      return `${code} ${price.toFixed(currencyFractionDigits(code))}`;
    }
  }

  getProductCurrencyCode(productId: number): string {
    return this.productsById.get(productId)?.currencyCode || 'USD';
  }

  getProductName(productId: number): string {
    return (
      this.productsById.get(productId)?.name ?? `Producto ID: ${productId}`
    );
  }

  getProductSku(productId: number): string {
    return this.productsById.get(productId)?.sku ?? '';
  }

  private actualizarValidacionesPrecio(): void {
    const priceType = this.itemForm.get('priceType')?.value;
    const priceCtrl = this.itemForm.get('price');
    const discPctCtrl = this.itemForm.get('discountPercent');
    const discAmtCtrl = this.itemForm.get('discountAmount');

    // Limpiar todos primero
    priceCtrl?.clearValidators();
    discPctCtrl?.clearValidators();
    discAmtCtrl?.clearValidators();

    if (priceType === 'fixed') {
      priceCtrl?.setValidators([Validators.required, Validators.min(0.01)]);
    } else if (priceType === 'discount_percent') {
      discPctCtrl?.setValidators([
        Validators.required,
        Validators.min(0.01),
        Validators.max(100),
      ]);
    } else if (priceType === 'discount_amount') {
      discAmtCtrl?.setValidators([Validators.required, Validators.min(0.01)]);
    }

    priceCtrl?.updateValueAndValidity();
    discPctCtrl?.updateValueAndValidity();
    discAmtCtrl?.updateValueAndValidity();
  }

  // Precio base (normal) del producto
  getProductBasePrice(productId: number): number | undefined {
    return this.productsById.get(productId)?.price;
  }

  getItemTotal(it: PriceListItemResponse): number | undefined {
    if (it.resolvedPrice === undefined || it.resolvedPrice === null)
      return undefined;
    const qty = it.minQuantity && it.minQuantity > 0 ? it.minQuantity : 1;
    return it.resolvedPrice * qty;
  }

  getPriceTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      fixed: 'Precio fijo',
      discount_percent: 'Descuento %',
      discount_amount: 'Descuento monto',
    };
    return labels[type] ?? type;
  }

  private convertirMonto(
    monto: number,
    monedaOrigenCode: string,
    monedaDestinoCode: string,
  ): number {
    if (!monto || monedaOrigenCode === monedaDestinoCode) return monto;

    const tasaOrigen = this.obtenerRateUSD(monedaOrigenCode);
    const tasaDestino = this.obtenerRateUSD(monedaDestinoCode);

    if (!tasaOrigen || !tasaDestino) return monto;

    const montoEnUSD = monto / tasaOrigen;
    return montoEnUSD * tasaDestino;
  }

  private obtenerRateUSD(monedaCode: string): number | null {
    const currency = this.currencies.find((c) => c.code === monedaCode);
    if (!currency?.rateUsd) return null;
    const rate = Number(currency.rateUsd);
    return isNaN(rate) || rate <= 0 ? null : rate;
  }

  get monedaListaCode(): string {
    const currencyId = this.form.get('currencyId')?.value;
    return (
      this.currencies.find((c) => c.id === currencyId)?.code ??
      this.currencies.find((c) => c.id === this.businessCurrency)?.code ??
      'USD'
    );
  }

  private recalcularPreciosPorMoneda(): void {
    if (this.items.length === 0) return;

    const monedaDestino = this.monedaListaCode;

    this.items = this.items.map((it) => ({
      ...it,
      resolvedPrice: this.calcularResolvedPrice(it, monedaDestino),
    }));
  }

  private calcularResolvedPrice(
    it: PriceListItemResponse,
    monedaDestino: string,
  ): number | undefined {
    const producto = this.productsById.get(it.productId);
    if (!producto) return it.resolvedPrice;

    const baseConvertida = this.convertirMonto(
      producto.price,
      producto.currencyCode ?? 'USD',
      monedaDestino,
    );

    switch (it.priceType) {
      case 'fixed':
        return it.price ?? baseConvertida;
      case 'discount_percent':
        return (
          baseConvertida - (baseConvertida * (it.discountPercent ?? 0)) / 100
        );
      case 'discount_amount': {
        const descuentoConvertido = this.convertirMonto(
          it.discountAmount ?? 0,
          producto.currencyCode ?? 'USD',
          monedaDestino,
        );
        return baseConvertida - descuentoConvertido;
      }
      default:
        return baseConvertida;
    }
  }
}
